import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import { confirmDeposit, createDeposit, getDeposit, getDeposits, quoteDeposit } from '../../services/DepositsService';
import './Deposit.css';

const PRESETS = [50, 100, 250, 500, 1000, 2000];

const STATUS_LABEL = {
    PENDING: 'Aguardando pagamento',
    PAID: 'Pago',
    CREDITED: 'Creditado',
    FAILED: 'Não aprovado',
    EXPIRED: 'Expirado',
    CANCELED: 'Cancelado'
};

function money(value, digits = 2) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '--';
    return number.toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function apiError(err, fallback) {
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data.message === 'string') return data.message;
    return err?.message || fallback;
}

function when(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function statusClass(status) {
    if (status === 'CREDITED') return 'is-credited';
    if (status === 'FAILED' || status === 'EXPIRED' || status === 'CANCELED') return 'is-failed';
    return 'is-pending';
}

function Deposit() {
    const isCustody = localStorage.getItem('isCustody') === 'true';
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const handledReturn = useRef(false);
    const [amount, setAmount] = useState('100');
    const [quote, setQuote] = useState(null);
    const [quoteError, setQuoteError] = useState('');
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quoting, setQuoting] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [checkingId, setCheckingId] = useState(null);
    const [notice, setNotice] = useState({ type: '', text: '' });

    async function load() {
        const data = await getDeposits();
        setOverview(data);
        return data;
    }

    useEffect(() => {
        if (!isCustody) {
            setLoading(false);
            return undefined;
        }
        let alive = true;
        load()
            .catch(err => {
                if (alive) setNotice({ type: 'error', text: apiError(err, 'Não foi possível carregar o depósito.') });
            })
            .finally(() => {
                if (alive) setLoading(false);
            });
        return () => { alive = false; };
    }, [isCustody]);

    useEffect(() => {
        if (!isCustody) return undefined;
        const normalized = String(amount).trim().replace(',', '.');
        if (!/^\d+(\.\d{1,2})?$/.test(normalized) || Number(normalized) <= 0) {
            setQuote(null);
            setQuoteError('');
            setQuoting(false);
            return undefined;
        }
        let alive = true;
        setQuoting(true);
        const timer = setTimeout(() => {
            quoteDeposit(normalized)
                .then(data => {
                    if (!alive) return;
                    setQuote(data);
                    setQuoteError('');
                })
                .catch(err => {
                    if (!alive) return;
                    setQuote(null);
                    setQuoteError(apiError(err, 'Cotação indisponível.'));
                })
                .finally(() => { if (alive) setQuoting(false); });
        }, 350);
        return () => {
            alive = false;
            clearTimeout(timer);
        };
    }, [amount, isCustody]);

    useEffect(() => {
        if (!isCustody || handledReturn.current) return undefined;
        const status = searchParams.get('status');
        const sessionId = searchParams.get('session_id');
        if (!status) return undefined;
        handledReturn.current = true;
        let alive = true;

        async function finishReturn() {
            if (status === 'cancel') {
                setNotice({ type: 'error', text: 'Pagamento cancelado. Nenhum valor entrou na custódia.' });
                navigate('/deposito', { replace: true });
                return;
            }
            if (status !== 'success' || !sessionId) {
                navigate('/deposito', { replace: true });
                return;
            }
            setSubmitting(true);
            try {
                const result = await confirmDeposit(sessionId);
                let deposit = result?.deposit;
                if (deposit && deposit.status !== 'CREDITED' && deposit.status !== 'FAILED' && deposit.status !== 'EXPIRED')
                    deposit = await waitUntilSettled(deposit.id);
                if (!alive) return;
                if (result?.custodyUsdc != null) {
                    setOverview(current => ({ ...(current || {}), custodyUsdc: result.custodyUsdc }));
                }
                await load();
                if (deposit?.status === 'CREDITED' || result?.credited) {
                    setNotice({
                        type: 'success',
                        text: `Pagamento confirmado. ${money(deposit?.amountUsdc ?? result?.deposit?.amountUsdc)} USDC entrou no saldo de custódia.`
                    });
                } else {
                    setNotice({
                        type: 'success',
                        text: 'Pagamento ainda não compensado. No boleto e no PIX, o USDC entra quando o Stripe confirmar.'
                    });
                }
            } catch (err) {
                if (alive) setNotice({ type: 'error', text: apiError(err, 'Não foi possível confirmar o pagamento.') });
            } finally {
                if (alive) {
                    setSubmitting(false);
                    navigate('/deposito', { replace: true });
                }
            }
        }

        finishReturn();
        return () => { alive = false; };
    }, [isCustody, navigate, searchParams]);

    async function onSubmit(event) {
        event.preventDefault();
        setSubmitting(true);
        try {
            const result = await createDeposit(String(amount).trim().replace(',', '.'));
            if (!result?.url)
                throw new Error('O Stripe não abriu o pagamento.');
            window.location.assign(result.url);
        } catch (err) {
            setNotice({ type: 'error', text: apiError(err, 'Não foi possível iniciar o depósito.') });
            setSubmitting(false);
        }
    }

    async function onCheck(deposit) {
        if (!deposit?.sessionId) return;
        setCheckingId(deposit.id);
        try {
            const result = await confirmDeposit(deposit.sessionId);
            await load();
            if (result?.credited || result?.deposit?.status === 'CREDITED') {
                setNotice({
                    type: 'success',
                    text: `${money(result.deposit?.amountUsdc)} USDC entrou no saldo de custódia.`
                });
            } else {
                setNotice({ type: 'error', text: 'O Stripe ainda não confirmou este pagamento.' });
            }
        } catch (err) {
            setNotice({ type: 'error', text: apiError(err, 'Não foi possível verificar o pagamento.') });
        } finally {
            setCheckingId(null);
        }
    }

    const configured = overview?.configured !== false;
    const deposits = overview?.deposits || [];

    return (
        <>
            <Menu />
            <main className="content">
                <div className="deposit-page">
                    <div className="deposit-header">
                        <div>
                            <h1>Depósito</h1>
                            <p>Pague em reais com cartão, PIX ou boleto. O valor em USDC entra no saldo de custódia quando o Stripe confirmar o pagamento.</p>
                        </div>
                        <Link className="deposit-back" to="/wallet">Ver carteira</Link>
                    </div>

                    {!isCustody ? (
                        <section className="deposit-card">
                            <p className="deposit-note">Depósito só está disponível para contas de custódia.</p>
                        </section>
                    ) : loading ? (
                        <p className="deposit-empty">Carregando...</p>
                    ) : (
                        <>
                            <div className="deposit-grid">
                                <section className="deposit-card">
                                    <div className="deposit-balance">
                                        <span>Saldo de custódia</span>
                                        <strong>{money(overview?.custodyUsdc)} USDC</strong>
                                    </div>
                                    <form className="deposit-form" onSubmit={onSubmit}>
                                        <label htmlFor="deposit-amount">Valor em reais</label>
                                        <input
                                            id="deposit-amount"
                                            className="deposit-amount"
                                            inputMode="decimal"
                                            autoComplete="off"
                                            value={amount}
                                            onChange={event => setAmount(event.target.value)}
                                            disabled={!configured || submitting}
                                        />
                                        <div className="deposit-presets">
                                            {PRESETS.map(preset => (
                                                <button
                                                    key={preset}
                                                    type="button"
                                                    className={Number(String(amount).replace(',', '.')) === preset ? 'is-active' : ''}
                                                    onClick={() => setAmount(String(preset))}
                                                    disabled={!configured || submitting}
                                                >
                                                    R$ {preset}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="deposit-quote">
                                            <span>Você recebe</span>
                                            <strong>
                                                {quoting ? 'Calculando...' : quote ? `${money(quote.amountUsdc)} USDC` : '--'}
                                            </strong>
                                            <p>
                                                {quote
                                                    ? `1 USDC ≈ R$ ${money(quote.fxRate)}. A cotação fica travada neste pagamento.`
                                                    : (quoteError || 'Informe um valor para ver quantos USDC entram na custódia.')}
                                            </p>
                                        </div>
                                        <button className="deposit-submit" type="submit" disabled={!configured || submitting || !quote}>
                                            {submitting ? 'Abrindo pagamento...' : 'Pagar com Stripe'}
                                        </button>
                                        {!configured ? (
                                            <p className="deposit-warning">O Stripe ainda não está configurado neste servidor.</p>
                                        ) : (
                                            <p className="deposit-note">
                                                O saldo só muda depois que o Stripe confirmar o pagamento. Mínimo de R$ {money(overview?.limits?.minBrl)} e máximo de R$ {money(overview?.limits?.maxBrl)}.
                                            </p>
                                        )}
                                    </form>
                                </section>
                                <section className="deposit-card">
                                    <h2 style={{ marginTop: 0, color: '#fff', fontSize: '1.15rem' }}>Como funciona</h2>
                                    <p className="deposit-note">1. Você escolhe o valor em reais.</p>
                                    <p className="deposit-note">2. O Stripe cobra no cartão, no PIX ou no boleto.</p>
                                    <p className="deposit-note">3. Com o pagamento confirmado, os USDC entram no saldo livre da custódia e passam a contar como capital depositado.</p>
                                </section>
                            </div>

                            <section className="deposit-history">
                                <h2>Seus depósitos</h2>
                                {deposits.length ? deposits.map(item => (
                                    <div className="deposit-row" key={item.id}>
                                        <div>
                                            <strong>R$ {money(item.amountBrl)} → {money(item.amountUsdc)} USDC</strong>
                                            <p>{when(item.creditedAt || item.createdAt)}</p>
                                        </div>
                                        <div className="deposit-side">
                                            <span className={`deposit-status ${statusClass(item.status)}`}>
                                                {STATUS_LABEL[item.status] || item.status}
                                            </span>
                                            {item.status !== 'CREDITED' && item.sessionId ? (
                                                <button
                                                    type="button"
                                                    className="deposit-check"
                                                    onClick={() => onCheck(item)}
                                                    disabled={checkingId === item.id}
                                                >
                                                    {checkingId === item.id ? 'Verificando...' : 'Verificar pagamento'}
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>
                                )) : (
                                    <p className="deposit-empty">Nenhum depósito ainda.</p>
                                )}
                            </section>
                        </>
                    )}
                </div>
                <Footer />
            </main>
            <Toast type={notice.type} text={notice.text} />
        </>
    );
}

async function waitUntilSettled(id) {
    for (let attempt = 0; attempt < 8; attempt += 1) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const data = await getDeposit(id);
        const deposit = data?.deposit;
        if (!deposit) return null;
        if (deposit.status === 'CREDITED' || deposit.status === 'FAILED' || deposit.status === 'EXPIRED')
            return deposit;
    }
    const data = await getDeposit(id);
    return data?.deposit || null;
}

export default Deposit;
